import { getFirestore, collection, doc, getDoc, getDocs, query, where, orderBy, limit, addDoc, Timestamp } from 'firebase/firestore';
import { dataApp } from '../config/firebase';
import { Product } from '../types/product';
import { ProductVersion, VersionMetadata } from '../types/productVersion';

const db = getFirestore(dataApp);

function serializeForFirestore(obj: any): any {
  if (obj === null || obj === undefined) {
    return null;
  }

  if (obj instanceof Date) {
    return Timestamp.fromDate(obj);
  }

  if (obj instanceof Timestamp) {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map(item => serializeForFirestore(item));
  }

  if (typeof obj === 'object') {
    const result: any = {};
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        result[key] = serializeForFirestore(obj[key]);
      }
    }
    return result;
  }

  return obj;
}

class ProductVersionService {
  private static instance: ProductVersionService;
  private readonly versionsCollection = 'productVersions';

  private constructor() {}

  static getInstance(): ProductVersionService {
    if (!ProductVersionService.instance) {
      ProductVersionService.instance = new ProductVersionService();
    }
    return ProductVersionService.instance;
  }

  async createVersion(
    product: Product,
    type: 'import' | 'optimization' | 'manual',
    marketplace?: 'shopify' | 'amazon' | 'googleShopping',
    notes?: string
  ): Promise<string> {
    try {
      const latestVersion = await this.getLatestVersion(product.id);
      const newVersionNumber = latestVersion ? latestVersion.versionNumber + 1 : 1;

      const versionData = {
        productId: product.id,
        versionNumber: newVersionNumber,
        timestamp: Timestamp.now(),
        type,
        ...(marketplace && { marketplace }),
        data: {
          source: product.source,
          basicInfo: product.basicInfo,
          links: product.links,
          images: product.images,
          status: product.status,
          storeName: product.storeName,
          optimizations: product.optimizations || {},
          ...(product.publishTimestamps && Object.keys(product.publishTimestamps).length > 0 && {
            publishTimestamps: product.publishTimestamps
          })
        },
        ...(notes && { notes })
      };

      // Serialize all data for Firestore
      const serializedData = serializeForFirestore(versionData);

      // Create the document
      const docRef = await addDoc(collection(db, this.versionsCollection), serializedData);
      return docRef.id;
    } catch (error) {
      console.error('Error creating product version:', error);
      throw error;
    }
  }

  async getVersions(productId: string): Promise<ProductVersion[]> {
    try {
      const q = query(
        collection(db, this.versionsCollection),
        where('productId', '==', productId),
        orderBy('versionNumber', 'desc')
      );

      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          productId: data.productId,
          versionNumber: data.versionNumber,
          timestamp: data.timestamp.toDate(),
          type: data.type,
          marketplace: data.marketplace,
          data: data.data,
          createdBy: data.createdBy,
          notes: data.notes
        };
      });
    } catch (error) {
      console.error('Error getting product versions:', error);
      throw error;
    }
  }

  async getLatestVersion(productId: string): Promise<ProductVersion | null> {
    try {
      const q = query(
        collection(db, this.versionsCollection),
        where('productId', '==', productId),
        orderBy('versionNumber', 'desc'),
        limit(1)
      );

      const querySnapshot = await getDocs(q);
      if (querySnapshot.empty) return null;

      const doc = querySnapshot.docs[0];
      const data = doc.data();
      return {
        id: doc.id,
        productId: data.productId,
        versionNumber: data.versionNumber,
        timestamp: data.timestamp.toDate(),
        type: data.type,
        marketplace: data.marketplace,
        data: data.data,
        createdBy: data.createdBy,
        notes: data.notes
      };
    } catch (error) {
      console.error('Error getting latest version:', error);
      throw error;
    }
  }

  async getVersion(versionId: string): Promise<ProductVersion | null> {
    try {
      const docRef = doc(db, this.versionsCollection, versionId);
      const docSnap = await getDoc(docRef);
      
      if (!docSnap.exists()) return null;

      const data = docSnap.data();
      return {
        id: docSnap.id,
        productId: data.productId,
        versionNumber: data.versionNumber,
        timestamp: data.timestamp.toDate(),
        type: data.type,
        marketplace: data.marketplace,
        data: data.data,
        createdBy: data.createdBy,
        notes: data.notes
      };
    } catch (error) {
      console.error('Error getting version:', error);
      throw error;
    }
  }

  async getVersionMetadata(productId: string): Promise<VersionMetadata[]> {
    try {
      const q = query(
        collection(db, this.versionsCollection),
        where('productId', '==', productId),
        orderBy('versionNumber', 'desc')
      );

      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => {
        const data = doc.data();
        const metadata: VersionMetadata = {
          id: doc.id,
          versionNumber: data.versionNumber,
          timestamp: data.timestamp.toDate(),
          type: data.type,
          marketplace: data.marketplace,
          createdBy: data.createdBy,
          notes: data.notes
        };

        if (data.type === 'import' && data.versionNumber === 1) {
          metadata.data = data.data;
        }

        return metadata;
      });
    } catch (error) {
      console.error('Error getting version metadata:', error);
      throw error;
    }
  }
}

export const productVersionService = ProductVersionService.getInstance();
